import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    Output,
    OnInit,
} from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { FormHelper } from '@klickdata/core/form';
import { MessageSavedComponent, MessageService } from '@klickdata/core/message';
import { MessageErrorComponent } from '@klickdata/core/message/src/message-error/message-error.component';
import { SideNaveData } from '@klickdata/core/mobile';
import { SideNaveResponseData, SideNaveActionsTypes } from '@klickdata/core/mobile/src/mobile.service';
import { User, UserService } from '@klickdata/core/user';
import { UserNotesService } from '@klickdata/core/user-notes';
import { UserData } from '@klickdata/core/user/src/user.model';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, filter, map, startWith, switchMap, takeUntil } from 'rxjs/operators';
import {
    MatSnackBar,
    MatSnackBarHorizontalPosition,
    MatSnackBarRef,
    MatSnackBarVerticalPosition,
} from '@angular/material/snack-bar';
import { AuthService } from '@klickdata/core/auth';
import { Utils } from '@klickdata/core/util';
import { AppScope } from '@klickdata/core/resource';

@Component({
    selector: 'app-menu-side-add-user',
    templateUrl: './menu-side-add-user.component.html',
    styleUrls: ['./menu-side-add-user.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MenuSideAddUserComponent implements OnDestroy, OnInit {
    @Input() public navData: SideNaveData;
    @Output() onClose: EventEmitter<SideNaveResponseData> = new EventEmitter();
    public userForm: FormGroup;
    public formats: { [key: string]: string };
    public destroy: Subject<boolean> = new Subject<boolean>();
    public loading$: Observable<boolean>;
    public error: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    public authUser: User;
    public userRoles: { [key: string]: string }[];
    protected position: {
        verticalPosition: MatSnackBarVerticalPosition;
        horizontalPosition: MatSnackBarHorizontalPosition;
    } = {
        verticalPosition: 'bottom',
        horizontalPosition: 'center',
    };
    private messageConfig = {
        duration: 6000,
        verticalPosition: this.position.verticalPosition,
        horizontalPosition: this.position.horizontalPosition,
        panelClass: ['right-direction', 'container_bg'],
    };

    constructor(
        protected fb: FormBuilder,
        protected userService: UserService,
        protected route: ActivatedRoute,
        protected router: Router,
        protected notesService: UserNotesService,
        protected message: MessageService,
        protected cd: ChangeDetectorRef
    ) {
        this.buildForm();
        this.formats = {
            fname: $localize`First name`,
            lname: $localize`Last name`,
            username: $localize`User name`,
            email: $localize`Email`,
            role_value: $localize`Learner role`,
        };
    }

    ngOnInit(): void {
        this.authUser = this.navData.data.user;
        // this.userRoles = Utils.getUserRoles(this.authUser.role_value, this.navData.data.customer?.division_mode);
        const roles = Utils.getUserRolesRelativeTo({
            role: this.authUser.role_value,
            allUserRolesSpecs: this.authUser.allUserRolesSpecs(),
            orEqual: false,
            returnFullSpecs: true,
            customerHasNODivisionMode: !this.navData.data.customer?.division_mode,
            comparison: 'lower',
        });
        this.userRoles = roles.map((role) => ({
            value: role.value,
            label: role.title,
        }));
        if (this.isDivisionMode) {
            const teamCtrl = this.teamCtrl;
            teamCtrl.setValidators(Validators.required);
            teamCtrl.updateValueAndValidity();
        }

        this.listenToFormChanges();
    }

    get isDivisionMode(): boolean {
        return !!this.navData?.data?.customer?.division_mode;
    }

    get groupAdminRoleSelectable(): boolean {
        return !this.isDivisionMode || !!this.teamCtrl?.value;
    }

    private get teamCtrl(): AbstractControl {
        return this.userForm.get('team_id');
    }

    private get roleCtrl(): AbstractControl {
        return this.userForm.get('role_value');
    }

    private get groupCtrl(): AbstractControl {
        return this.userForm.get('group_id');
    }
    get noGroupsAvailableMessage(): string {
        return this.isDivisionMode
            ? $localize`No ${this.groupDynamicName} available under this ${this.divisionDynamicName}`
            : undefined;
    }
    get noDivisionsAvailableMessage(): string {
        return this.isDivisionMode ? $localize`No ${this.divisionDynamicName} available` : undefined;
    }
    get learnerDynamicName(): string {
        return this.navData?.data?.customer.scope_names[AppScope.USERS] ?? AppScope.label(AppScope.USERS);
    }
    get groupDynamicName(): string {
        return this.navData?.data?.customer.scope_names[AppScope.GROUPS] ?? AppScope.label(AppScope.GROUPS);
    }
    get divisionDynamicName(): string {
        return this.navData?.data?.customer.scope_names[AppScope.TEAM] ?? AppScope.label(AppScope.TEAM);
    }

    private listenToFormChanges(): void {
        this.userForm.valueChanges
            .pipe(startWith(this.userForm.value), takeUntil(this.destroy))
            .subscribe(() => this.cd.markForCheck());

        this.teamCtrl.valueChanges.pipe(takeUntil(this.destroy)).subscribe((teamId) => this.onTeamChange(teamId));

        this.roleCtrl.valueChanges.pipe(takeUntil(this.destroy)).subscribe((role) => this.onRoleChange(role));
    }

    private onTeamChange(teamId: string | null): void {
        if (teamId) {
            this.groupCtrl.patchValue(null);
            return;
        }

        const isGroupAdmin = this.roleCtrl.value === 'groupadmin';
        if (this.isDivisionMode && isGroupAdmin) {
            this.roleCtrl.patchValue('user', { emitEvent: false });
        }
    }

    private onRoleChange(role: string): void {
        const teamSelected = this.teamCtrl.value !== null && this.teamCtrl.value !== undefined;
        if (this.isDivisionMode && role === 'groupadmin' && !teamSelected) {
            this.roleCtrl.patchValue('user', { emitEvent: false });
        }
    }

    protected buildForm(): void {
        this.userForm = this.fb.group(
            {
                fname: ['', Validators.required],
                lname: ['', Validators.required],
                email: ['', [Validators.email, Validators.required]],
                // username: ['', Validators.required],
                role_value: ['user', Validators.required],
                notes: [''],
                managed_groups: [[]],
                managed_divisions: [[]],
                team_id: [null],
                group_id: [null],
            }
            // { validators: [this.userValidator()] }
        );
    }
    userValidator(): ValidatorFn {
        return (ctrl: AbstractControl): ValidationErrors | null => {
            return ctrl.value.role_value === 'groupadmin' && !ctrl.value.managed_groups?.length
                ? { forbidden: { message: $localize`Managed groups is required!` } }
                : null;
        };
    }
    public submit(sendActivation?: boolean): void {
        this.checkSubmitValid()
            .pipe(
                filter((isValid) => !!isValid),
                switchMap(() => this.prepareSubmit()),
                filter((user) => user && !!user.id),
                switchMap((user) =>
                    this.userForm.value.notes && !!this.userForm.value.notes.length
                        ? this.notesService
                              .addHrNote({
                                  body: this.userForm.value.notes,
                                  user_id: user.id,
                              })
                              .pipe(map(() => user))
                        : of(user)
                ),
                filter((user) => !!user),
                switchMap((user) => {
                    const msgSendReciever = this.userService.send({ users_attach: [user.id] }).pipe(
                        filter((msg) => msg.sent),
                        map(() => user)
                    );
                    return sendActivation ? msgSendReciever : of(user);
                }),
                takeUntil(this.destroy),
                catchError((err) => this.handleCatchedError(err))
            )
            .subscribe(
                (fetchedUser) => this.onSuccess(fetchedUser, sendActivation),
                (err) => {
                    if (err && err.error && err.error.error) {
                        this.error.next(err.error.error.messages);
                    }
                    this.loading$ = of(false);
                }
            );
    }
    handleCatchedError(err: any) {
        if (err && err.error && err.error.error) {
            this.error.next(err.error.error.messages);
            this.message.openMessage(MessageErrorComponent, err.error.error.messages.join('/n'), this.messageConfig);
        }
        this.loading$ = of(false);
        return of(null);
    }
    private checkSubmitValid(): Observable<boolean> {
        if (this.userForm.invalid && this.userForm.errors && this.userForm.errors?.forbidden) {
            this.message.openMessage(MessageErrorComponent, this.userForm.errors.forbidden.message, this.messageConfig);
            return of(false);
        }
        return of(true);
    }
    private prepareSubmit(): Observable<User> {
        this.loading$ = of(true);
        const user = this.prepareForm();
        return this.userService.create(user);
    }

    protected prepareForm(): UserData {
        const data: UserData = {
            fname: this.userForm.value.fname,
            lname: this.userForm.value.lname,
            role_value: this.userForm.value.role_value,
            email: this.userForm.value.email.trim(),
            username: this.userForm.value.email.trim(),
        };
        if (this.userForm.value.managed_groups?.length) {
            data.managed_groups = this.userForm.value.managed_groups;
        }
        if (this.userForm.value.managed_divisions?.length) {
            data.managed_divisions = this.userForm.value.managed_divisions;
        }
        const teamId = this.userForm.value.team_id;
        const groupId = this.userForm.value.group_id;
        if (teamId != null) {
            data.team_id = teamId;
        }
        if (groupId != null) {
            data.group_id = groupId;
        }
        const user = new User(data).getPayload();
        user.customer_id = this.navData.data.user.customer_id;
        return user;
    }
    protected onSuccess(user: User, sendActivation?: boolean): void {
        if (!user?.id) {
            this.loading$ = of(false);
            return;
        }
        this.message.openMessage(
            MessageSavedComponent,
            sendActivation
                ? $localize`A new learner created with an activation mail has been sent`
                : $localize`A new learner has been created`,
            this.messageConfig
        );
        this.loading$ = of(false);
        if (!sendActivation && user?.id) {
            this.router.navigate(
                this.navData.data.context === 'customer'
                    ? ['/master/customers/k3-connections/connection', user.id]
                    : ['/admin/accounts/users/users', user.id]
            );
        }
        this.userForm.reset({
            role_value: 'user',
            team_id: null,
            group_id: null,
            managed_groups: [],
            managed_divisions: [],
        });
        this.error.next(null);
        this.onClose.emit({ action: SideNaveActionsTypes.POSITIVE, data: user });
    }
    public ngOnDestroy(): void {
        this.destroy.next(true);
        this.destroy.unsubscribe();
    }
}
